import api, { refreshAuthToken } from './api';
import type { LoginRequest, LoginResponse, LoginResult, MfaVerifyResponse, MfaStatusResponse, User } from '@/types/user.types';
import type { ApiResponse } from '@/types/api.types';

/** GET /auth/config — public deployment flags and the password rules (QA #63 / #67). */
export interface AuthConfig {
  saml_enabled: boolean;
  watcher_bundle_enabled?: boolean;
  /** PASSWORD_MIN_LENGTH (default 12) */
  password_min_length?: number;
  /** PASSWORD_MAX_AGE_DAYS (default 90; 0 = no expiry) */
  password_max_age_days?: number;
  /** PASSWORD_REQUIRE_COMPLEXITY (default true; QA #63「高強度」) */
  password_require_complexity?: boolean;
  /** UPLOAD_BLOCKED_EXTENSIONS — file types the upload endpoints refuse (QA #74) */
  upload_blocked_extensions?: string[];
  /** ALLOWED_USER_EMAIL_DOMAINS — domains a user's e-mail may use; empty = any (QA #91) */
  allowed_user_email_domains?: string[];
  /** ALLOWED_ALERT_EMAIL_DOMAINS — domains alert mail may go to; empty = any (QA #94) */
  allowed_alert_email_domains?: string[];
  /**
   * UPLOAD_RESUME_WINDOW in seconds (ADR-054, default 14400 = 4h): how long a
   * chunked upload cut off by a network outage waits to resume. 0 = no wait.
   */
  upload_resume_window_seconds?: number;
}

/** GET /auth/password-setup/:token — what a usable 設定密碼 link says about itself (QA #75). */
export interface SetupTokenInfo {
  valid: true;
  username: string;
  /** invite = account just created; reset = 寄送設定密碼信 on an existing account */
  kind: 'invite' | 'reset';
  expires_at: string;
}

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

export const authService = {
  /**
   * Login with username and password
   * Returns either a normal LoginResponse or an MfaRequiredResponse
   */
  async login(credentials: LoginRequest): Promise<LoginResult> {
    // Mock 模式
    if (USE_MOCK) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          // 驗證帳號密碼
          if (credentials.username === 'admin' && credentials.password === 'admin123') {
            const mockResponse: LoginResponse = {
              token: 'mock-jwt-token-' + Date.now(),
              expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
              user: {
                id: 'mock-uuid-admin',
                username: 'admin',
                email: 'admin@example.com',
                role: 'admin',
                is_active: true,
                must_change_password: false,
                groups: [{ id: 'mock-group-default', name: 'Default Group' }],
                primary_group: { id: 'mock-group-default', name: 'Default Group' },
              },
            };
            resolve(mockResponse);
          } else {
            reject(new Error('帳號或密碼錯誤'));
          }
        }, 500);
      });
    }

    // 真實 API
    const response = await api.post<ApiResponse<LoginResult>>(
      '/api/v1/auth/login',
      credentials
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Login failed');
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await api.post('/api/v1/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API response (ADR-046: the refresh
      // token too — the server has revoked the session).
      this.clearAuthData();
    }
  },

  /**
   * Get current user profile
   */
  // Self-service e-mail change (decision D1 / QA #2). Username, role and
  // groups stay admin-managed. skipGlobalError: the Profile page shows the
  // USER_EXISTS / INVALID_INPUT outcome inline.
  async updateProfile(email: string): Promise<User> {
    const response = await api.put<ApiResponse<User>>(
      '/api/v1/auth/profile',
      { email },
      { skipGlobalError: true },
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('更新個人資料失敗');
  },

  async getProfile(): Promise<User> {
    const response = await api.get<ApiResponse<User>>('/api/v1/auth/profile');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to get profile');
  },

  /**
   * Change password
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const response = await api.put<ApiResponse>(
      '/api/v1/auth/change-password',
      {
        old_password: oldPassword,
        new_password: newPassword,
      }
    );

    if (!response.data.success) {
      throw new Error('Failed to change password');
    }
  },

  /**
   * Verify MFA code
   */
  async verifyMfa(mfaToken: string, code: string): Promise<MfaVerifyResponse> {
    const response = await api.post<ApiResponse<MfaVerifyResponse>>('/api/v1/auth/verify-mfa', {
      mfa_token: mfaToken,
      code,
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'MFA verification failed');
  },

  /**
   * Resend MFA verification code
   */
  async resendMfa(mfaToken: string): Promise<void> {
    const response = await api.post<ApiResponse>('/api/v1/auth/resend-mfa', {
      mfa_token: mfaToken,
    });
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to resend');
    }
  },

  /**
   * Enable MFA for current user
   */
  async enableMfa(): Promise<void> {
    const response = await api.post<ApiResponse>('/api/v1/auth/enable-mfa');
    if (!response.data.success) {
      throw new Error('Failed to enable MFA');
    }
  },

  /**
   * Disable MFA for current user (requires password confirmation)
   */
  async disableMfa(password: string): Promise<void> {
    const response = await api.post<ApiResponse>('/api/v1/auth/disable-mfa', { password });
    if (!response.data.success) {
      throw new Error('Failed to disable MFA');
    }
  },

  /**
   * Get MFA status for current user
   */
  async getMfaStatus(): Promise<MfaStatusResponse> {
    const response = await api.get<ApiResponse<MfaStatusResponse>>('/api/v1/auth/mfa-status');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    return { mfa_enabled: false, mfa_method: 'email' };
  },

  /**
   * QA #75: check a 設定密碼 link before showing the form (public). A dead
   * link rejects with code SETUP_TOKEN_INVALID / SETUP_TOKEN_EXPIRED and the
   * backend's Chinese message; the page shows it inline (skipGlobalError).
   */
  async verifySetupToken(token: string): Promise<SetupTokenInfo> {
    const response = await api.get<ApiResponse<SetupTokenInfo>>(
      `/api/v1/auth/password-setup/${encodeURIComponent(token)}`,
      { skipGlobalError: true },
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('設定密碼連結無效');
  },

  /**
   * QA #75: set the password from a 設定密碼 link (public). Policy errors
   * (PASSWORD_TOO_SHORT / PASSWORD_SAME_AS_OLD) and a dead token come back as
   * an Error carrying .code and the backend's message.
   */
  async completePasswordSetup(token: string, newPassword: string): Promise<void> {
    const response = await api.post<ApiResponse>(
      '/api/v1/auth/password-setup',
      { token, new_password: newPassword },
      { skipGlobalError: true },
    );
    if (!response.data.success) {
      throw new Error(response.data.error?.message || '設定密碼失敗');
    }
  },

  /**
   * Login methods this deployment offers (public, no token). The Azure AD
   * button on the login page shows only when saml_enabled (ADR-017, Q6).
   */
  async getAuthConfig(): Promise<AuthConfig> {
    const response = await api.get<ApiResponse<AuthConfig>>('/api/v1/auth/config', { skipGlobalError: true });
    return response.data.data ?? { saml_enabled: false };
  },

  /**
   * Get current auth token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem('token');
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  },

  /**
   * Save auth data to localStorage
   */
  saveAuthData(token: string, user: User, refreshToken?: string): void {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    // ADR-046: the refresh token lives next to the access token (same threat
    // model as before; httpOnly cookie is a follow-up ADR).
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  },

  /**
   * ADR-046: renew the access token with the stored refresh token (single-
   * flight). Used by the idle timer while the user is active.
   */
  refreshSession(): Promise<string | null> {
    return refreshAuthToken();
  },

  clearAuthData(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refresh_token');
  },

  /**
   * Get auth data from localStorage
   */
  getAuthData(): { token: string | null; user: User | null } {
    const token = this.getToken();
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    return { token, user };
  },
};
